import React, { FC, useMemo, useContext } from "react";
import { useDropzone } from "react-dropzone";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import {
	TrainingCourseDuration,
	TrainingCourseRequest,
} from "@/features/training/model/Training/TrainingCourse";
import { CustomCloseButton, CustomSaveButton } from "@/features/training/ui";
import CustomInput from "@/shared/forms/CustomInput";
import CustomSelect from "@/shared/forms/CustomSelect";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
	Autocomplete,
	Box,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { useFetchGroups } from "@/service/GroupQueries";
import { useMutateCreateTrainingCourse } from "@/service/TrainingMutations";
import {
	useFetchCourseFolders,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface NewTrainingCourseProps {
	handleClose: () => void;
	open: boolean;
}

interface GroupOption {
	id: number;
	name: string;
}

interface TrainerOption {
	Id: number;
	Name: string;
}

interface FolderOption {
	Id: number;
	Name: string;
}

const ACCEPTED_TYPES = {
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog shell                                                                 */
/* ──────────────────────────────────────────────────────────────────────────── */

export const NewTrainingCourse: FC<NewTrainingCourseProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="new-course-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="new-course-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L137C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate String="Training.NewTrainingCourse.Title">
						Add Course
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						lineHeight: 1.5,
						pr: 5,
					}}
				>
					<Translate String="Training.NewTrainingCourse.Subtitle">
						Set up course details, ownership, competency requirements, and
						attachments.
					</Translate>
				</Typography>

				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L164C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<NewTrainingCourseForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                         */
/* ──────────────────────────────────────────────────────────────────────────── */

type FormValues = TrainingCourseRequest & { FolderId: number | null };

const validationSchema = Yup.object().shape({
	Name: Yup.string().trim().required("Name is required"),
});

const NewTrainingCourseForm: FC<NewTrainingCourseProps> = (props) => {
	// ✅ Hooks at top level (always called)
	const { isLoading: isLoadingTrainerOptions, data: trainerOptionsRaw } =
		useFetchUserSummariesByCurrentCompany();
	const { isLoading: isLoadingFolders, data: foldersRaw } =
		useFetchCourseFolders();
	const { isLoading: isLoadingGroups, data: groupsRaw } = useFetchGroups();

	const createTrainingCourse = useMutateCreateTrainingCourse();

	const trainerOptions: TrainerOption[] = Array.isArray(trainerOptionsRaw)
		? (trainerOptionsRaw as TrainerOption[])
		: [];
	const folders: FolderOption[] = Array.isArray(foldersRaw)
		? (foldersRaw as FolderOption[])
		: [];
	const groups = Array.isArray(groupsRaw) ? groupsRaw : [];

	// ✅ useMemo must NOT be after a conditional return
	const competenceGroups: GroupOption[] = useMemo(() => {
		return groups
			.filter((x) => x.GroupModules?.Competence)
			.map((x) => ({ id: x.Id, name: x.Name ?? "" }));
	}, [groups]);

	const isLoading =
		isLoadingTrainerOptions ||
		isLoadingFolders ||
		isLoadingGroups ||
		!groupsRaw;

	if (isLoading) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const initialValues: FormValues = {
		Name: "",
		Description: null,
		Duration: TrainingCourseDuration.OneYear,
		CourseManagerId: trainerOptions[0]?.Id ?? 0,
		FolderId: folders.length > 0 ? folders[0].Id : null,
		Attachments: [],
		NewAttachment: null,
		GroupsWithCompetencyRequirement: [],
	};

	const handleSubmit = async (values: FormValues) => {
		const folderId =
			values.FolderId != null && values.FolderId !== 0 ? values.FolderId : 0;

		try {
			await createTrainingCourse({
				Name: values.Name,
				Description: values.Description,
				Duration: values.Duration,
				CourseManagerId: values.CourseManagerId,
				Attachments: values.Attachments,
				NewAttachment: values.NewAttachment,
				FolderId: folderId,
				GroupsWithCompetencyRequirement: values.GroupsWithCompetencyRequirement,
			});

			props.handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			props.handleClose();
			AlertRequest.failed();
		}
	};

	return (
		<Formik<FormValues>
			initialValues={initialValues}
			validationSchema={validationSchema}
			validateOnMount
			onSubmit={handleSubmit}
		>
			{(formik) => (
				<CourseFormInner
					{...formik}
					onClose={props.handleClose}
					competenceGroups={competenceGroups}
					trainerOptions={trainerOptions}
					folders={folders}
				/>
			)}
		</Formik>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Inner form component (safe place for hooks like useDropzone)                 */
/* ──────────────────────────────────────────────────────────────────────────── */

type CourseFormInnerProps = {
	onClose: () => void;
	competenceGroups: GroupOption[];
	trainerOptions: TrainerOption[];
	folders: FolderOption[];
	setFieldValue: (
		field: string,
		value: unknown,
		shouldValidate?: boolean,
	) => void;
	values: FormValues;
	isValid: boolean;
	dirty: boolean;
	isSubmitting: boolean;
};

const CourseFormInner: FC<CourseFormInnerProps> = ({
	onClose,
	competenceGroups,
	trainerOptions,
	folders,
	setFieldValue,
	values,
	isValid,
	dirty,
	isSubmitting,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const selectedGroupIds = Array.isArray(values.GroupsWithCompetencyRequirement)
		? values.GroupsWithCompetencyRequirement.map(Number)
		: [];
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 2, sm: 2.5 },
	};

	const selectAllGroups = () => {
		setFieldValue(
			"GroupsWithCompetencyRequirement",
			competenceGroups.map((g) => g.id),
		);
	};

	const clearAllGroups = () => {
		setFieldValue("GroupsWithCompetencyRequirement", []);
	};

	// ✅ Hook is now inside a real component (safe)
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (file) setFieldValue("NewAttachment", file);
		},
		accept: { ...ACCEPTED_TYPES },
		maxSize: MAX_FILE_SIZE,
		maxFiles: 1,
		multiple: false,
		onDropRejected: (rejections) => {
			const err = rejections[0]?.errors?.[0];
			if (err?.code === "file-too-large") {
				void AlertToast.failed("File exceeds 5 MB limit");
			} else if (err?.code === "file-invalid-type") {
				void AlertToast.failed("Invalid file type. Use PDF, DOC, JPG or PNG.");
			}
		},
	});

	return (
		<Form>
			<Stack spacing={3}>
				{/* 1. Course Information */}
				<Paper variant="outlined" sx={sectionCardSx}>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						<Translate String="Training.NewTrainingCourse.Section.CourseInfo">
							Course Information
						</Translate>
					</Typography>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mb: 2 }}
					>
						<Translate String="Training.NewTrainingCourse.Section.CourseInfo.Desc">
							Name and describe the course.
						</Translate>
					</Typography>

					<Stack spacing={2}>
						<CustomInput
							id="Name"
							type="text"
							label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L392C14.Name", "Name *")}
							name="Name"
							translationString="Training.NewTrainingCourse.Name"
							placeholder={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L395C20.EnterCourseName", "Enter course name")}
							autoFocus
							required
							style={{ width: "100%" }}
						/>

						<SATOSEditorField
							id="description"
							name="Description"
							label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L404C14.Description", "Description")}
							aria-required={true}
							aria-describedby="description-error-text"
							style={{ minWidth: "100%" }}
							imageUploadDisabled={true}
						/>
					</Stack>
				</Paper>

				{/* 2. Settings */}
				<Paper variant="outlined" sx={sectionCardSx}>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						<Translate String="Training.NewTrainingCourse.Section.Settings">
							Settings
						</Translate>
					</Typography>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mb: 2 }}
					>
						<Translate String="Training.NewTrainingCourse.Section.Settings.Desc">
							Duration, manager and folder.
						</Translate>
					</Typography>

					<Stack spacing={2}>
						<CustomSelect
							label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L433C14.Duration", "Duration")}
							name="Duration"
							id="Duration"
							translationString="Training.NewTrainingCourse.Duration"
							style={{ width: "100%" }}
						>
							{Object.values(TrainingCourseDuration).map((duration) => (
								<option value={duration} key={duration}>
									{duration.replace(/([A-Z])/g, " $1").trim()}
								</option>
							))}
						</CustomSelect>

						<CustomSelect
							label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L447C14.Manager", "Manager")}
							name="CourseManagerId"
							id="CourseManagerId"
							translationString="Training.NewTrainingCourse.Manager"
							style={{ width: "100%" }}
						>
							<option value={0}><Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L453C26.SelectManager">Select manager...</Translate></option>
							{trainerOptions.map((trainer) => (
								<option value={trainer.Id} key={trainer.Id}>
									{trainer.Name}
								</option>
							))}
						</CustomSelect>

						<CustomSelect
							label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L462C14.Folder", "Folder")}
							name="FolderId"
							id="FolderId"
							translationString="Training.NewTrainingCourse.Folder"
							style={{ width: "100%" }}
						>
							<option value={0}><Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L468C26.General">General</Translate></option>
							{folders.map((folder) => (
								<option value={folder.Id} key={folder.Id}>
									{folder.Name}
								</option>
							))}
						</CustomSelect>
					</Stack>
				</Paper>

				{/* 3. Groups With Competency Requirement */}
				<Paper variant="outlined" sx={sectionCardSx}>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, ml: 0.5 }}>
						<Translate String="Training.NewTrainingCourse.GroupsWithCompetencyRequirement">
							Groups With Competency Requirement
						</Translate>
						{selectedGroupIds.length > 0 && (
							<Typography
								component="span"
								variant="body2"
								color="primary"
								sx={{ ml: 1, fontWeight: 600 }}
							>
								({selectedGroupIds.length} selected)
							</Typography>
						)}
					</Typography>

					<Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
						<ProcedureButton
							size="small"
							variant="outlined"
							onClick={selectAllGroups}
						>
							<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L501C8.SelectAll">Select all</Translate>
						</ProcedureButton>
						<ProcedureButton
							size="small"
							variant="outlined"
							intent="warning"
							onClick={clearAllGroups}
						>
							<Translate String="Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L509C8.ClearAll">Clear all</Translate>
						</ProcedureButton>
					</Stack>

					<Autocomplete
						multiple
						options={competenceGroups}
						getOptionLabel={(opt) => opt.name}
						value={competenceGroups.filter((g) =>
							selectedGroupIds.includes(g.id),
						)}
						onChange={(_, newVal) => {
							setFieldValue(
								"GroupsWithCompetencyRequirement",
								newVal.map((g) => g.id),
							);
						}}
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Actions.NewTrainingCourse.L531C21.SearchOrSelectGroups", "Search or select groups...")}
								size="small"
							/>
						)}
						renderOption={(props, option, { selected }) => (
							<li
								{...props}
								key={option.id}
								style={{ borderBottom: "1px solid", borderColor: "divider" }}
							>
								<Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
								{option.name}
							</li>
						)}
					/>
				</Paper>

				{/* 4. Attachments */}
				<Paper variant="outlined" sx={sectionCardSx}>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						<Translate String="Training.NewTrainingCourse.Attachments">
							Attachments
						</Translate>
					</Typography>

					<Box
						{...getRootProps()}
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							border: "2px dashed",
							borderColor: isDragActive ? "primary.main" : "divider",
							borderRadius: 2,
							py: 3,
							px: 2,
							textAlign: "center",
							cursor: "pointer",
							bgcolor: isDragActive ? "action.hover" : "background.default",
							color: "text.secondary",
							"&:hover": {
								borderColor: "primary.main",
								bgcolor: "action.hover",
							},
						}}
					>
						<input {...getInputProps()} id="new-course-attachment" />

						{values.NewAttachment ? (
							<Stack
								direction="row"
								alignItems="center"
								spacing={2}
								sx={{
									p: 2,
									borderRadius: 1,
									bgcolor: "background.paper",
									width: "100%",
									maxWidth: 400,
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<InsertDriveFileOutlinedIcon
									sx={{ color: "text.secondary", fontSize: 32 }}
								/>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography
										variant="body2"
										sx={{
											fontWeight: 600,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{values.NewAttachment.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{formatFileSize(values.NewAttachment.size)}
									</Typography>
								</Box>
								<IconButton
									onClick={(e) => {
										e.stopPropagation();
										setFieldValue("NewAttachment", null);
									}}
									color="error"
									size="small"
									aria-label="Remove file"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
						) : (
							<>
								<Typography variant="body2" sx={{ mb: 0.5 }}>
									<Translate String="Training.NewTrainingCourse.DropZone.Drag">
										Drag & drop files here
									</Translate>
								</Typography>
								<Typography variant="body2">
									<Translate String="Training.NewTrainingCourse.DropZone.Or">
										or
									</Translate>{" "}
									<Box
										component="span"
										sx={{ textDecoration: "underline", color: "primary.main" }}
									>
										<Translate String="Shift.Event.Choose.File">
											Choose files
										</Translate>
									</Box>
								</Typography>
							</>
						)}
					</Box>
				</Paper>
			</Stack>

			<DialogActions
				sx={{
					...sharedTaskDialogActionsSx(theme, false),
					px: 0,
					pt: 2,
					mt: 3,
					display: "flex",
					justifyContent: "flex-end",
					gap: 1.5,
					flexWrap: "wrap",
				}}
			>
				<CustomCloseButton handleClose={onClose} disabled={isSubmitting} />

				<CustomSaveButton
					loading={isSubmitting}
					disabled={!isValid || !dirty || isSubmitting}
				/>
			</DialogActions>
		</Form>
	);
};
